import { unstable_batchedUpdates } from 'react-dom'
import { dbStorage } from './indexedDBStorage'
import { useAnchoredRowsStore } from '../store/anchoredRowsStore'
import {
  prepareWitnessProcessingResultRecord,
  useBooksStore,
  type Witness,
} from '../store/booksStore'
import { useCharacterLayerStore } from '../store/characterLayerStore'
import type { ChapterCharacterLayer } from '../types/characterLayerTypes'
import {
  serializeAnchoredRow,
  type AnchoredRow,
} from '../types/anchoredRowTypes'

export interface LedgerSafeSegmentationEditPersistenceResult {
  updatedWitness: Witness
  updatedRows: AnchoredRow[]
}

/**
 * Persist every durable projection of one segmentation edit in a single
 * PostgreSQL-primary / IndexedDB-cache transaction, then publish the committed
 * records to the in-memory stores. No store state changes before the durable
 * transaction succeeds.
 */
export const persistLedgerSafeSegmentationEditAtomically = async ({
  witness,
  nextSegmentationResult,
  changedRows,
  characterLayer,
}: {
  witness: Witness
  nextSegmentationResult: Witness['segmentation_result']
  changedRows: AnchoredRow[]
  characterLayer?: ChapterCharacterLayer
}): Promise<LedgerSafeSegmentationEditPersistenceResult> => {
  const booksState = useBooksStore.getState()
  const currentWitness = booksState.getWitness(witness.id) || witness
  const chapter = booksState.getChapter(currentWitness.chapter_id)
  const updatedWitness = prepareWitnessProcessingResultRecord({
    witness: currentWitness,
    chapterBookId: chapter?.book_id,
    update: {
      witnessId: currentWitness.id,
      type: 'segmentation',
      result: nextSegmentationResult,
    },
  })
  const persistedAt = new Date().toISOString()
  const updatedRows = changedRows.map(row => ({
    ...row,
    updatedAt: persistedAt,
  }))

  await dbStorage.putManyAcrossStores({
    witnesses: [updatedWitness as any],
    ...(updatedRows.length > 0
      ? { anchored_rows: updatedRows.map(serializeAnchoredRow) }
      : {}),
    ...(characterLayer
      ? { chapter_character_layers: [characterLayer] }
      : {}),
  })

  unstable_batchedUpdates(() => {
    useBooksStore.setState(state => ({
      witnesses: state.witnesses.map(candidate => (
        candidate.id === updatedWitness.id ? updatedWitness : candidate
      )),
      error: null,
    }))

    if (updatedRows.length > 0) {
      const updatedRowsById = new Map(updatedRows.map(row => [row.id, row]))
      useAnchoredRowsStore.setState(state => {
        const existingRows = state.chapterRows.get(witness.chapter_id) || []
        const nextRows = existingRows
          .map(row => updatedRowsById.get(row.id) || row)
          .sort((left, right) => left.position - right.position)
        const chapterRows = new Map(state.chapterRows)
        chapterRows.set(witness.chapter_id, nextRows)
        return { chapterRows }
      })
    }

    if (characterLayer) {
      useCharacterLayerStore.setState(state => ({
        chapterLayers: new Map(state.chapterLayers).set(
          characterLayer.chapter_id,
          characterLayer,
        ),
      }))
    }
  })

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('witness-updated', {
      detail: { witnessId: updatedWitness.id, version: updatedWitness.version },
    }))
  }

  return { updatedWitness, updatedRows }
}
